import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Order } from '@/types';
import { Search, Package, AlertCircle, RotateCw } from 'lucide-react';
import { useCustomerContext } from "@/context/CustomerContext";
import { useAuth } from '@/context/AuthContext';


const OrderHistory: React.FC = () => {
  const { orders, isLoading, refreshData } = useCustomerContext();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter orders based on search (by product title or order id)
  const filteredOrders = orders.filter((order: Order) => {
    return (
      order.product_title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.order_id.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: 'spring', stiffness: 100, damping: 15 },
    },
  };

  if (isLoading && orders.length === 0) {
    return (
      <div className="tw-min-h-screen tw-flex tw-justify-center tw-items-center tw-h-64">
        <div className="tw-w-12 tw-h-12 tw-border-t-4 tw-border-[#00C5FB] tw-rounded-full tw-animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="tw-space-y-6 tw-bg-[#202020] tw-min-h-screen tw-p-6"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="tw-flex tw-justify-between tw-items-center"
      >
        <div>
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-100">
            Order History
          </h1>
          <p className="tw-text-gray-300">
            All your purchases with Protega insurance
          </p>
        </div>
      </motion.div>

      {/* Search & Orders */}
      <motion.div
        variants={itemVariants}
        className="tw-bg-[#202020] tw-rounded-lg tw-overflow-hidden"
      >
        <div className="tw-flex tw-flex-col lg:tw-flex-row tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-gap-4">
          <div className="tw-flex tw-w-full lg:tw-w-auto tw-gap-2">
            <div className="tw-relative tw-flex-1 lg:tw-w-64">
              <Search className="tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-500" />
              <input
                type="text"
                placeholder="Search orders by name or ID"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="tw-block tw-w-full tw-pl-10 tw-pr-3 tw-py-2 tw-bg-[#1c1c1e] tw-border tw-border-gray-700 tw-rounded-md tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-cyan-500 focus:tw-border-cyan-500 sm:tw-text-sm tw-text-gray-100"
              />
            </div>
            <button
              onClick={() => refreshData()}
              disabled={isLoading}
              className={`tw-p-2 tw-bg-[#1c1c1e] tw-border tw-border-gray-700 tw-rounded-md tw-text-gray-400 hover:tw-text-[#00C5FB] hover:tw-border-[#00C5FB] tw-transition-all ${isLoading ? "tw-opacity-50 tw-cursor-not-allowed" : ""}`}
              title="Refresh Orders"
            >
              <RotateCw className={`tw-w-5 tw-h-5 ${isLoading ? "tw-animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Order cards */}
        {filteredOrders.length > 0 ? (
          <div className="tw-grid tw-grid-cols-1 tw-gap-6 tw-py-4">
            {filteredOrders.map((order: Order) => (
              <div key={`${order.order_id}-${order.product_id}`} className="tw-block">
                <OrderCard order={order} />
              </div>
            ))}
          </div>
        ) : (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
            <AlertCircle className="tw-w-12 tw-h-12 tw-text-gray-500" />
            <h3 className="tw-mt-2 tw-text-sm tw-font-medium tw-text-gray-100">
              No orders found
            </h3>
            <p className="tw-mt-1 tw-text-sm tw-text-gray-400">
              {searchQuery
                ? `No orders matching "${searchQuery}"`
                : "You haven't made any purchases with Protega yet."}
            </p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

interface OrderCardProps {
  order: Order;
}

const formatDate = (dateString: string) => {
  try {
    return new Intl.DateTimeFormat("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(dateString));
  } catch {
    return "N/A";
  }
};

const OrderCard: React.FC<OrderCardProps> = ({ order }) => {
  const navigate = useNavigate();
  const { loginType } = useAuth();

  const formattedOrderId = `ORD-${order.order_id.slice(-10).toUpperCase()}`;
  const purchaseDateStr = formatDate(order.created_at);

  const handleProductClick = () => {
    if (loginType === 'token') {
      navigate(`/customer/orders/${order.order_id}/fix-issue`);
    } else {
      // navigate(`/customer/orders/${order.order_id}`);
    }
  };

  // Helper to determine the order tags
  const getOrderTags = () => {
    const tags: { duration: string; title: string; validTill: string }[] = [];

    // Tag 1: Eligibility status - Only show if returns_active is true
    if (order.returns_active) {
      tags.push({
        duration: "Eligible",
        title: "Return Window",
        validTill: "Active Coverage",
      });
    }

    // Tag 2: Return request status
    const hasReturnRaised = order.return_status && String(order.return_status) !== 'false';
    if (hasReturnRaised) {
      const statusStr = typeof order.return_status === 'string'
        ? order.return_status.charAt(0).toUpperCase() + order.return_status.slice(1).replace('_', ' ')
        : 'Pending';

      tags.push({
        duration: statusStr,
        title: "Return Status",
        validTill: `RT-${order.order_id.slice(-6).toUpperCase()}`,
      });
    } else if (order.returns_active) {
      tags.push({
        duration: "No Request",
        title: "Return Request",
        validTill: "Not Raised Yet",
      });
    }

    // Tag 3: Associated Policies
    if (order.policy) {
      const policiesArray = Array.isArray(order.policy) ? order.policy : [order.policy];

      policiesArray.forEach((pol) => {
        if (!pol) return;
        const startDate = new Date(pol.start_date);
        const endDate = new Date(pol.end_date);
        const diffTime = endDate.getTime() - startDate.getTime();
        const diffDays = Math.round(diffTime / (24 * 60 * 60 * 1000));
        const diffMonths = Math.round(diffDays / 30.4375);
        const diffYears = Math.round(diffDays / 365);

        let durationLabel = "";
        if (diffYears >= 1 && Math.abs(diffDays - diffYears * 365) < 30) {
          durationLabel = `${diffYears}-Year`;
        } else {
          durationLabel = `${diffMonths}-Months`;
        }

        let policyTitle = "Product Protection";
        if (pol.policy_type === 'protection') {
          policyTitle = "Accidental Damage Protection";
        } else if (pol.policy_type === 'manufacturing') {
          policyTitle = "Manufacturing Warranty";
        } else if (pol.policy_type === 'extended') {
          policyTitle = "Extended Warranty";
        } else if (pol.policy_type === 'shipping') {
          policyTitle = "Shipping Protection";
          durationLabel = "Transit";
        }

        tags.push({
          duration: durationLabel,
          title: policyTitle,
          validTill: `Valid till ${formatDate(pol.end_date)}`,
        });
      });
    }

    return tags;
  };

  const tagsList = getOrderTags();

  const getStatusTextAndStyles = () => {
    // 1. Return request status
    const hasReturnRaised = order.return_status && String(order.return_status) !== 'false';
    if (hasReturnRaised) {
      const text = typeof order.return_status === 'string'
        ? order.return_status.charAt(0).toUpperCase() + order.return_status.slice(1).replace('_', ' ')
        : 'Pending';
      if (order.return_status === 'approved') {
        return { text, className: 'tw-bg-[#A3F3BE] tw-text-[#14532D]' };
      } else if (order.return_status === 'rejected') {
        return { text, className: 'tw-bg-red-950 tw-text-red-200' };
      } else {
        return { text, className: 'tw-bg-yellow-950 tw-text-yellow-200' };
      }
    }

    // 2. Policy status
    if (order.policy) {
      const policiesArray = Array.isArray(order.policy) ? order.policy : [order.policy];
      const isActive = policiesArray.some(p => p && new Date(p.end_date).getTime() > Date.now());
      return {
        text: isActive ? 'Active Policy' : 'Expired Policy',
        className: isActive ? 'tw-bg-[#A3F3BE] tw-text-[#14532D]' : 'tw-bg-gray-700 tw-text-gray-300'
      };
    }

    // 3. Return eligibility status
    if (order.returns_active) {
      return { text: 'Eligible', className: 'tw-bg-[#A3F3BE] tw-text-[#14532D]' };
    }

    return { text: 'Closed', className: 'tw-bg-gray-700 tw-text-gray-300' };
  };

  const statusBadge = getStatusTextAndStyles();

  return (
    <div className="tw-relative tw-bg-[#2c2c2e] tw-border tw-border-gray-800 tw-rounded-2xl tw-p-6 tw-flex tw-flex-col md:tw-flex-row tw-items-center md:tw-items-start tw-gap-6 tw-transition-all hover:tw-border-gray-700">
      {/* Left: Product Image in White Square */}
      <div className="tw-w-[130px] tw-h-[130px] tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center tw-bg-white tw-rounded-2xl tw-p-3 tw-cursor-pointer" onClick={handleProductClick}>
        {order.product_image ? (
          <img
            src={order.product_image}
            alt={order.product_title}
            className="tw-w-full tw-h-full tw-object-contain"
          />
        ) : (
          <Package className="tw-w-10 tw-h-10 tw-text-gray-400" />
        )}
      </div>

      {/* Center: Details and protections */}
      <div className="tw-flex-1 tw-space-y-4 tw-w-full">
        <div className="tw-space-y-1">
          <h3 className="tw-text-xl tw-font-bold tw-line-clamp-2 tw-text-white tw-cursor-pointer" onClick={handleProductClick}>
            {order.product_title}
          </h3>
          <p className="tw-text-xs tw-text-gray-400">
            Order ID: <span className="tw-text-gray-300">{formattedOrderId}</span> • Purchased on {purchaseDateStr}
          </p>
        </div>

        {tagsList.length > 0 && (
          <div className="tw-space-y-2">
            <h4 className="tw-text-xs tw-font-semibold tw-text-gray-300 tw-uppercase tw-tracking-wider">
              Details
            </h4>
            <div className="tw-flex tw-flex-wrap tw-gap-3">
              {tagsList.map((tag, idx) => (
                <div key={idx} className="tw-bg-[#404040]/50 tw-border tw-border-gray-600/20 tw-rounded-lg tw-p-2 tw-px-3 tw-flex-none tw-w-[170px] tw-text-left">
                  <div className="tw-text-[11px] tw-font-semibold tw-text-white">
                    {tag.duration}
                  </div>
                  <div className="tw-text-[11px] tw-text-gray-300 tw-truncate" title={tag.title}>
                    {tag.title}
                  </div>
                  <div className="tw-text-[9px] tw-text-gray-400 tw-mt-0.5">
                    {tag.validTill}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Status Badge - Absolutely positioned */}
      <span className={`tw-absolute tw-top-6 tw-right-6 tw-text-xs tw-font-semibold tw-px-2.5 tw-py-1 tw-rounded-md ${statusBadge.className}`}>
        {statusBadge.text}
      </span>

      {/* Right: Action Buttons */}
      <div className="tw-flex tw-flex-col tw-justify-end tw-w-full md:tw-w-64 tw-mt-6 md:tw-mt-10 tw-flex-shrink-0">
        <div className="tw-flex tw-flex-col tw-space-y-2.5 tw-w-full">
          {order.returns_active && (
            <button
              type="button"
              disabled={!!order.return_status && String(order.return_status) !== 'false' && (typeof order.return_status !== 'string' || !['requested', 'approved'].includes(order.return_status.toLowerCase()))}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (order.return_status && order.return_status === 'approved') {
                  navigate(`/customer/orders/${order.order_id}/fix-issue`);
                } else {
                  navigate(`/customer/return-chat/${order.order_id}`);
                }
              }}
              className={`tw-w-full tw-px-6 tw-py-3.5 tw-rounded-xl tw-font-bold tw-text-sm tw-flex tw-items-center tw-justify-center tw-transition-colors tw-border-none tw-duration-500
                ${!!order.return_status && String(order.return_status) !== 'false' && (typeof order.return_status !== 'string' || !['requested', 'approved'].includes(order.return_status.toLowerCase()))
                  ? 'tw-bg-gray-600 tw-text-gray-400 tw-cursor-not-allowed'
                  : 'tw-bg-[#00C5FB] tw-text-gray-900 hover:tw-bg-white tw-cursor-pointer'
                }`}
            >
              {!!order.return_status && String(order.return_status) !== 'false' ? (
                (typeof order.return_status === 'string' && order.return_status.toLowerCase() === 'requested') ? (
                  "Continue Return"
                ) : (typeof order.return_status === 'string' && order.return_status.toLowerCase() === 'approved') ? (
                  "Proceed to Resolution"
                ) : (
                  "Return Requested"
                )
              ) : 'Request Return'}
            </button>
          )}

          {/* <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              navigate(`/customer/orders/${order.order_id}/fix-issue`);
            }}
            className="tw-w-full tw-border tw-border-gray-500 tw-text-gray-300 hover:tw-bg-[#404040]/40 tw-font-medium tw-px-6 tw-py-3 tw-rounded-xl tw-text-sm tw-flex tw-items-center tw-justify-center tw-transition-all"
          >
            View Order Details
          </button> */}
        </div>
      </div>
    </div>
  );
};

export default OrderHistory;
